import {useCallback, useEffect, useMemo, useState} from "react";
import {useSetting} from "contexts/setting";
import {useBgUrl} from "hooks/useBgUrl";
import {palettes} from "data/palettes";
import {extractPaletteFromImageUrl} from "libs/pixelation";

// Neutral fallback so the effect still paints if a product has no palette and
// the background image can't be sampled (e.g. CORS-tainted upload preview).
export const FALLBACK_PALETTE = ["#ffffff", "#c0c0c0", "#808080", "#404040", "#000000"];

export type PixelationPaletteResult = {
  /** Effective palette the mosaic is drawn from for the active source — the
   * user's edited override if one exists, else the resolved default. */
  palette: string[];
  /** The active source's resolved DEFAULT colors (sampled bg / brand palette),
   * ignoring any user override. Used as the seed when the user first edits. */
  defaultPalette: string[];
  /** True when the active source currently has a user override stored. */
  isCustomized: boolean;
};

/**
 * Resolves the pixelation overlay's color palette for the active source —
 * shared by PixelationLayer (to draw) and PixelationControls (to show/edit
 * swatches) so the two never drift. Owns the background-image color sampling.
 *
 * Each source ("background"/"product") has a resolved default; the user can
 * override it (see `pixelationPalettes` in the setting context). Overrides are
 * stored per source, so edits to one persist while the other is active, and
 * survive switching back and forth. The Reset button clears all overrides.
 */
export function usePixelationPalette(): PixelationPaletteResult {
  const {product, pixelationEnabled, pixelationSource, pixelationPalettes} = useSetting();
  const {url: bgUrl} = useBgUrl();

  const [bgPalette, setBgPalette] = useState<string[]>([]);

  const productPalette = useMemo(() => palettes[product.label] || [], [product.label]);

  // Sample the background image whenever the effect is on and a bg exists —
  // not only when "background" is the active source — so the Background tab's
  // default colors are ready the moment it's selected. Best effort: an empty
  // result (no bg, or tainted canvas) leaves bgPalette empty and resolution
  // falls back to the product palette.
  useEffect(() => {
    if (!pixelationEnabled || !bgUrl) {
      setBgPalette([]);
      return;
    }
    let cancelled = false;
    extractPaletteFromImageUrl(bgUrl)
      .then((colors) => {
        if (!cancelled) setBgPalette(colors);
      })
      .catch(() => {
        if (!cancelled) setBgPalette([]);
      });
    return () => {
      cancelled = true;
    };
  }, [pixelationEnabled, bgUrl]);

  // Resolve a source to its DEFAULT colors. "product" → brand palette;
  // "background" → sampled bg colors. Both fall back to the other, then a
  // neutral ramp, so the mosaic never blanks.
  const resolveDefault = useCallback(
    (src: string) => {
      if (src === "product") {
        return productPalette.length
          ? productPalette
          : bgPalette.length
            ? bgPalette
            : FALLBACK_PALETTE;
      }
      return bgPalette.length
        ? bgPalette
        : productPalette.length
          ? productPalette
          : FALLBACK_PALETTE;
    },
    [productPalette, bgPalette]
  );

  const defaultPalette = useMemo(
    () => resolveDefault(pixelationSource),
    [resolveDefault, pixelationSource]
  );

  // A stored override for the active source wins over the default. Empty/absent
  // override ⇒ use the default. This is resolved identically wherever the hook
  // is used, so the controls' swatches match the mosaic exactly.
  const override = pixelationPalettes[pixelationSource];
  const isCustomized = Array.isArray(override) && override.length > 0;
  const palette = useMemo(
    () => (isCustomized ? override : defaultPalette),
    [isCustomized, override, defaultPalette]
  );

  return {palette, defaultPalette, isCustomized};
}
